import React, { useState } from 'react'
import { Box, Button, Paper, TextField, Typography } from '@material-ui/core'
import { Rating } from '@material-ui/lab'
import PropTypes from 'prop-types'
import { isEmpty } from 'lodash'

import { LoadingFeedback } from 'components'

import useFetch from 'hooks/useFetch'
import useSnackbar from 'hooks/useSnackbar'

import * as service from 'service'
import helpers from 'helpers'
import constants from 'constants/index'

const { TICKET_FEEDBACK_RATINGS } = constants.tickets
const { RATING_AND_COMMENT_MODE } = constants.evaluationSettings

const TicketFeedback = ({ ticketId, ticketFeedback, refresh }) => {
  const [stars, setStars] = useState(null)
  const [comment, setComment] = useState('')
  const [commentTouched, setCommentTouched] = useState(false)
  const [loading, setLoading] = useState(false)

  const snackbar = useSnackbar()

  const { response } = useFetch(
    service.dponet.evaluationSetting.show,
    {},
    [],
    !ticketFeedback,
  )
  const evaluationSetting = response?.data?.evaluationSetting

  if (ticketFeedback) {
    const starsGiven =
      TICKET_FEEDBACK_RATINGS.indexOf(ticketFeedback.rating) + 1

    return (
      <Paper component={Box} elevation={2} p={3}>
        <Typography variant="h5">Sua avaliação</Typography>

        <Box display="flex" py={2}>
          <Rating value={starsGiven} readOnly size="large" />
        </Box>

        {!isEmpty(ticketFeedback.comment) && (
          <TextField
            label="Comentário"
            multiline
            minRows={3}
            fullWidth
            value={ticketFeedback.comment}
            disabled
          />
        )}
      </Paper>
    )
  }

  if (!evaluationSetting || !evaluationSetting.enabled) return null

  const isCommentMode = evaluationSetting.mode === RATING_AND_COMMENT_MODE
  const commentMissing = isCommentMode && isEmpty(comment.trim())

  const submitFeedback = async (rating) => {
    try {
      setLoading(true)

      await service.dponet.tickets.createTicketFeedback({
        ticketId: ticketId,
        ticketFeedback: {
          rating,
          comment: isCommentMode ? comment.trim() : undefined,
        },
      })

      setLoading(false)
      refresh()

      snackbar.open({
        message: 'Obrigado pelo seu feedback!',
        variant: 'success',
      })
    } catch (error) {
      setLoading(false)
      snackbar.open({
        message: helpers.formatters.errorMessage(error.response.data.error),
        variant: 'error',
      })
    }
  }

  const handleRatingChange = (event, value) => {
    setStars(value)

    if (!isCommentMode && value)
      submitFeedback(TICKET_FEEDBACK_RATINGS[value - 1])
  }

  const handleSubmit = () => {
    if (!stars || commentMissing) return

    submitFeedback(TICKET_FEEDBACK_RATINGS[stars - 1])
  }

  return (
    <Paper component={Box} elevation={2} p={3}>
      <LoadingFeedback loading={loading} />

      <Typography variant="h5">Como você avalia nosso suporte?</Typography>

      <Box display="flex" py={2}>
        <Rating
          value={stars}
          onChange={handleRatingChange}
          disabled={loading}
          size="large"
        />
      </Box>

      {isCommentMode && (
        <Box display="flex" flexDirection="column" gridGap={16}>
          <TextField
            label="Comentário"
            placeholder="Conte como foi o atendimento..."
            multiline
            minRows={3}
            fullWidth
            value={comment}
            onChange={(event) => setComment(event.target.value)}
            onBlur={() => setCommentTouched(true)}
            error={commentTouched && commentMissing}
            helperText={
              commentTouched && commentMissing
                ? 'Comentário é obrigatório'
                : undefined
            }
            disabled={loading}
          />
          <Box display="flex" justifyContent="flex-end">
            <Button
              variant="contained"
              color="secondary"
              disabled={!stars || commentMissing || loading}
              onClick={handleSubmit}
            >
              ENVIAR AVALIAÇÃO
            </Button>
          </Box>
        </Box>
      )}
    </Paper>
  )
}

TicketFeedback.propTypes = {
  ticketId: PropTypes.oneOfType([PropTypes.string, PropTypes.number])
    .isRequired,
  ticketFeedback: PropTypes.object,
  refresh: PropTypes.func.isRequired,
}

export default TicketFeedback
